import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../hooks/useTranslation';

interface AgendaEvent {
  id: number;
  title: string;
  eventType: string;
  startsAt: string;
  locationName: string | null;
  color: string | null;
}

const TYPE_COLORS: Record<string, string> = {
  activity: '#16A34A',
  appointment: '#2563EB',
  reminder: '#DC2626',
  birthday: '#F59E0B',
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

// T6.7 / D8: `backdropFilter: 'blur(8px)'` was a no-op here because
// `var(--caro-card-bg)` is opaque in every theme (white in light,
// `#2D3748` in dark, `#000000` in HC). The blur cost GPU time without
// any visual payoff. Dropped — the card stays solid by design.
const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--caro-card-bg)',
  borderRadius: '16px',
  padding: '14px 20px',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid var(--caro-border)',
  height: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

/**
 * "Next Up" card shown on the home page.
 * Always renders — shows "No events" when empty.
 * Auto-detects container height to show 2-4 events.
 */
export function NextUpAgenda() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useApi<{ success: boolean; data: AgendaEvent[] }>('/api/v1/user/agenda/today');
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxEvents, setMaxEvents] = useState(2);

  const events = data?.data || [];
  const now = new Date();
  const upcoming = events
    .filter(e => new Date(e.startsAt) > now)
    .slice(0, maxEvents);

  // Detect tall containers to show more events
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        if (h > 200) setMaxEvents(4);
        else setMaxEvents(2);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Always render a card — even when no events
  if (upcoming.length === 0) {
    return (
      <div ref={containerRef} onClick={() => navigate('/today')} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: 'var(--font-heading)' }}>{'\u{1F4C5}'}</span>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 'var(--font-body)',
              fontWeight: 600,
              color: 'var(--caro-text)',
            }}>
              {t.today.title}
            </div>
            <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)' }}>
              {t.today.noEvents}
            </div>
          </div>
          <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)', flexShrink: 0 }}>
            &#x25B6;
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onClick={() => navigate('/today')}
      style={cardStyle}
    >
      {upcoming.map((event, i) => {
        const color = event.color || TYPE_COLORS[event.eventType] || 'var(--caro-primary)';
        return (
          <div
            key={event.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              paddingTop: i > 0 ? '8px' : 0,
              borderTop: i > 0 ? '1px solid var(--caro-border)' : 'none',
              marginTop: i > 0 ? '8px' : 0,
            }}
          >
            <div style={{
              width: '6px',
              height: '36px',
              borderRadius: '3px',
              backgroundColor: color,
              flexShrink: 0,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 'var(--font-body)',
                fontWeight: 600,
                color: 'var(--caro-text)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {event.title}
              </div>
              <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)' }}>
                {formatTime(event.startsAt)}
                {event.locationName && ` \u2014 ${event.locationName}`}
              </div>
            </div>
            <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)', flexShrink: 0 }}>
              &#x25B6;
            </div>
          </div>
        );
      })}
    </div>
  );
}
